import { HStack, VStack, IconButton, Text, type UseToastOptions } from '@chakra-ui/react';
import { Icon, type IconName } from './Icon';

/**
 * Yaxle toast (ENG-2278) — one card style for every status toast in the app.
 * Registered as the ChakraProvider `toastOptions` default render, so every
 * plain `useToast()({ title, status })` call site inherits it. Toasts sit
 * at the top of the viewport, centered horizontally (see
 * DEFAULT_TOAST_OPTIONS). Rich toasts that pass their own `render`
 * (notification popups, delivery board) are unaffected.
 */

type ToastRenderProps = UseToastOptions & { onClose: () => void };

const STATUS_ICON: Record<string, { name: IconName; color: string }> = {
    success: { name: 'checkCircle', color: 'success.fg' },
    error: { name: 'xCircle', color: 'error.fg' },
    warning: { name: 'alert', color: 'warning.fg' },
    info: { name: 'info', color: 'info.fg' },
    loading: { name: 'info', color: 'info.fg' },
};

export const YaxleToast = ({ title, description, status, isClosable, onClose }: ToastRenderProps) => {
    const statusStyle = STATUS_ICON[status ?? 'info'] ?? STATUS_ICON.info;
    return (
        <HStack
            bg="bg.surface"
            borderRadius="2xl"
            boxShadow="none"
            borderWidth="1px"
            borderColor="border.subtle"
            px={4}
            py={3}
            spacing={2.5}
            align={description ? 'flex-start' : 'center'}
            maxW="26.25rem"
        >
            <Icon
                name={statusStyle.name}
                color={statusStyle.color}
                size="lg"
                mt={description ? '0.125rem' : 0}
            />
            <VStack align="flex-start" spacing={0.5} flex="1" minW={0}>
                {title ? (
                    <Text fontWeight="semibold" color="fg.default" fontSize="md" lineHeight="short">
                        {title}
                    </Text>
                ) : null}
                {description ? (
                    <Text color="fg.muted" fontSize="sm" lineHeight="short">
                        {description}
                    </Text>
                ) : null}
            </VStack>
            {isClosable !== false && (
                <IconButton
                    aria-label="Dismiss"
                    icon={<Icon name="x" size="md" />}
                    size="xs"
                    variant="ghost"
                    color="fg.muted"
                    onClick={onClose}
                    flexShrink={0}
                    mt={description ? '1px' : 0}
                />
            )}
        </HStack>
    );
};

/**
 * Provider-level defaults: pinned to the top of the viewport and centered
 * horizontally. Stacked toasts grow downward from there.
 */
export const DEFAULT_TOAST_OPTIONS: UseToastOptions = {
    position: 'top',
    duration: 4000,
    isClosable: true,
    render: (props) => <YaxleToast {...props} />,
};

export default YaxleToast;
